/* Domain models for trucks information in entries ST12 */

/**
 * Domain model for endpoints that return a simple count of trucks
 * and the list of vehicle plate numbers currently in the plant/parking.
 *
 * Used by:
 *  - GET /entries/workshop/trucks-in-plant/:workshop_id/:min_dlz_hours
 *  - GET /entries/workshop/trucks-in-plant/parking
 */
export class TrucksInArea {
  /** Total number of trucks in the area */
  count: number;
  /** List of truck plate numbers currently in the area */
  transportIds: string[];
  /** Last update timestamp for the metric */
  lastUpdated: string;

  constructor(
    count: number = 0,
    transportIds: string[] = [],
    lastUpdated: string = ''
  ) {
    this.count = count;
    this.transportIds = transportIds;
    this.lastUpdated = lastUpdated;
  }
}

/**
 * Domain model for the average DLZ time per unloading point.
 *
 * Used by:
 *  - GET /entries/workshop/unloading-points/average-dlz
 */
export class UnloadingPointAverageDlz {
  /** Name / code of the unloading point, e.g. "T12M" */
  unloadingPoint: string;
  /** Average DLZ time (in minutes) for the given point */
  averageDlz: number;

  constructor(unloadingPoint: string = '', averageDlz: number = 0) {
    this.unloadingPoint = unloadingPoint;
    this.averageDlz = averageDlz;
  }
}

/**
 * Domain aggregate for average DLZ information across all unloading points.
 */
export class UnloadingPointsAverageDlzInfo {
  /** Human‑readable description of the metric, e.g. "Average dlz time in minutes" */
  metric: string;
  /** ISO timestamp when the information was last updated */
  lastUpdated: string;
  /** List of unloading points and their average DLZ */
  unloadingPoints: UnloadingPointAverageDlz[];

  constructor(
    metric: string = '',
    lastUpdated: string = '',
    unloadingPoints: UnloadingPointAverageDlz[] = []
  ) {
    this.metric = metric;
    this.lastUpdated = lastUpdated;
    this.unloadingPoints = unloadingPoints;
  }
}

/**
 * Domain model for information about trucks queued in a specific point
 * (e.g. unloading buffer, waiting‑to‑arrive buffer, etc.).
 *
 * Used inside:
 *  - TrucksInPlantByPointInfo
 */
export class TrucksInPointInfo {
  /** Name / code of the point, e.g. "T12A BUFFER" */
  point: string;
  /** Number of trucks currently associated to this point */
  count: number;
  /** List of truck plate numbers in this point */
  transportIds: string[];

  constructor(
    point: string = '',
    count: number = 0,
    transportIds: string[] = []
  ) {
    this.point = point;
    this.count = count;
    this.transportIds = transportIds;
  }
}

/**
 * Domain aggregate for endpoints that group trucks by point
 * (buffer, waiting‑to‑arrive, etc.).
 *
 * Used by:
 *  - GET /entries/workshop/trucks-in-plant/unloading-buffer
 *  - GET /entries/workshop/trucks-in-plant/waiting-to-arrive
 */
export class TrucksInPlantByPointInfo {
  /** Number of different points returned by the endpoint */
  totalPoints: number;
  /** Detailed information for each point */
  data: TrucksInPointInfo[];
  /** Last update timestamp for the metric */
  lastUpdated: string;

  constructor(
    totalPoints: number = 0,
    data: TrucksInPointInfo[] = [],
    lastUpdated: string = ''
  ) {
    this.totalPoints = totalPoints;
    this.data = data;
    this.lastUpdated = lastUpdated;
  }
}
